import { Injectable } from "../../dependencies/injectable";
import { Injected } from "../../dependencies/injected";
import { ERROR_MESSAGES } from "../../errors/default.errors.i18n";
import { ErrorCodes, ErrorWithCode } from "../../errors/errors";
import { Lang_fr_en } from "../../i18n/languages";
import { anyObject, getProp, setProp } from "../../tools/any.tools";
import { APP_SERVICE, AppService } from "../app.service";
import { ERROR_MESSAGE_SERVICE, ErrorMessageService } from "../errorMessage.service";

/**
 * Builds a DefaultErrorMessageService with 'fr' and 'en' messages and 'en' as default language
 */
export function buildDefaultErrorMessageService(): DefaultErrorMessageService<Lang_fr_en> {
    return new DefaultErrorMessageService<Lang_fr_en>(ERROR_MESSAGES);
}

/**
 * Default implementation for the ErrorMessageService
 */
@Injectable(ERROR_MESSAGE_SERVICE)
export class DefaultErrorMessageService<Lang extends string> implements ErrorMessageService<Lang> {

    @Injected(APP_SERVICE)
    protected readonly _app!: AppService;

    protected _errorMessages: Record<Lang, Record<number, string>>;

    /**
     * @param errorMessages The error messages
     * @throws an error if errorMessages does not contain a message for GENERIC_ERROR_CODE in defaultLang
     */
    constructor(errorMessages: Record<Lang, Record<number, string>>) {
        // clone errorMessages
        this._errorMessages = {} as Record<Lang, Record<number, string>>;
        for (const lang in errorMessages) {
            this._errorMessages[lang] = { ...errorMessages[lang] };
        }
    }

    /**
     * @throws an error if we have no error message for default lang
     * TODO call somewhere
     */
    public checkDefaultLang(): void {
        const defaultLang = this._app.lang;
        // check default lang and default message are within provided messages        
        if (!this._errorMessages || !getProp(this._errorMessages, defaultLang) || !getProp<anyObject>(this._errorMessages, defaultLang)[ErrorCodes.GENERIC_ERROR_CODE]) {
            console.error(this._errorMessages);
            throw new Error("Error messages must contain at least a message for code " + ErrorCodes.GENERIC_ERROR_CODE + " in default language " + defaultLang);
        }
    }

    /**
     * Get the error message in the required language from an error response
     * @param error The error object
     * @param lang language wanted for the error message
     */
    getErrorMessage(error: ErrorWithCode = { code: ErrorCodes.GENERIC_ERROR_CODE }, lang?: string): Promise<string> {
        // if no lang is provided use default lang
        const defaultLang = this._app.lang;
        if (lang == null) { lang = defaultLang; }

        // Get the message list by lang, if it does not exist for the given lang then using the default one
        const langRegistration = getProp<anyObject>(this._errorMessages, lang) ?? getProp(this._errorMessages, defaultLang);

        // Get the message for the given code, if does not exists then using the generic message
        const errorMessage = langRegistration[error.code] ?? langRegistration[ErrorCodes.GENERIC_ERROR_CODE];

        return Promise.resolve(errorMessage);
    }

    /**
     * Save all error messages per code and per Lang
     * @param overridingRegistration Contain a message for every response code
     */
    registerErrorMessages(overridingRegistration: Record<Lang, Record<number, string>>): void {
        Object.keys(overridingRegistration).forEach(lang => {
            setProp(this._errorMessages, lang, {
                ...getProp<anyObject>(this._errorMessages, lang),
                ...getProp<anyObject>(overridingRegistration, lang)
            });
        });
    }
}